import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { ArrowLeft } from 'lucide-react'
import setThemeColor from '../lib/themeColor'
import useEventRole from '../lib/useEventRole'
import PlaySheet from '../components/PlaySheet'

// Standalone PlaySheet view for an event — same component the GM sees
// inside the dashboard, surfaced as a route so the dossier hub can link
// to it from a closed event. By the time an event is closed every item
// shows as deployed; PlaySheet renders that gracefully (checkmark + dim).
const D = {
  page: { minHeight: '100vh', background: '#d8c898', color: '#1f1d18', fontFamily: "'Special Elite', 'Courier Prime', monospace" },
  nav: { background: '#1f1d18', borderBottom: '3px solid #c08a2a', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  display: { fontFamily: "'Stardos Stencil', 'Special Elite', serif" },
  body: { fontFamily: "'Special Elite', 'Courier Prime', monospace" },
  paper: { background: '#0a140a', border: '1px solid rgba(0,255,102,.18)', boxShadow: '0 2px 8px rgba(0,0,0,.18)' },
}

export default function EventPlaySheet() {
  const { id: eventId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const role = useEventRole(eventId)

  const [event, setEvent] = useState(null)
  const [bounties, setBounties] = useState([])
  const [factions, setFactions] = useState([])
  const [mcsNodes, setMcsNodes] = useState([])
  const [orgFeatures, setOrgFeatures] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => { setThemeColor('cipher') }, [])
  useEffect(() => { fetchAll() }, [eventId])

  const fetchAll = async () => {
    setLoading(true)
    const [{ data: ev }, { data: bs }, { data: fs }, { data: ns }] = await Promise.all([
      supabase.from('events').select('id, name, status, org_id, closed_at').eq('id', eventId).limit(1),
      supabase.from('bounties').select('*').eq('event_id', eventId),
      supabase.from('event_factions').select('*').eq('event_id', eventId),
      supabase.from('mcs_nodes').select('*').eq('event_id', eventId),
    ])
    if (!ev?.[0]) { navigate('/play'); return }
    setEvent(ev[0])
    setBounties(bs || [])
    setFactions(fs || [])
    setMcsNodes(ns || [])
    if (ev[0].org_id) {
      const { data } = await supabase.from('organizations').select('features').eq('id', ev[0].org_id).limit(1)
      setOrgFeatures(data?.[0]?.features || {})
    }
    setLoading(false)
  }

  if (loading || role.loading) return (
    <div style={{ ...D.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ ...D.display, fontSize: 14, color: '#7a7058', letterSpacing: '0.14em' }}>OPENING PLAY SHEET...</p>
    </div>
  )

  // Block non-GM viewers — the play sheet is GM/co-GM only.
  if (!(role.isFullGM || role.isCoGm)) {
    return (
      <div style={{ ...D.page, padding: '40px 20px', textAlign: 'center' }}>
        <p style={{ ...D.display, fontSize: 16, color: '#a82a1f', letterSpacing: '0.14em' }}>RESTRICTED</p>
        <p style={{ ...D.body, fontSize: 12, color: '#7a7058', marginTop: 8 }}>This dossier is GM-only.</p>
        <button onClick={() => navigate(-1)} style={{ marginTop: 16, ...D.body, fontSize: 11, color: '#c08a2a', background: 'none', border: '1px solid #c08a2a', padding: '8px 16px', cursor: 'pointer', letterSpacing: '0.14em', textTransform: 'uppercase' }}>BACK</button>
      </div>
    )
  }

  return (
    <div style={D.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Stardos+Stencil:wght@400;700&family=Special+Elite&family=Courier+Prime:wght@400;700&display=swap');`}</style>

      <div style={D.nav}>
        <div>
          <div style={{ ...D.display, color: '#f0e6c8', fontSize: 14, letterSpacing: '0.14em' }}>ANUBIS</div>
          <div style={{ color: '#c08a2a', fontSize: 9, letterSpacing: '0.12em', marginTop: 2 }}>FIELD OPS · PLAY SHEET</div>
        </div>
        <div style={{ color: '#c08a2a', fontSize: 9, letterSpacing: '0.08em' }}>
          {event?.name?.toUpperCase() || 'NO EVENT'}
        </div>
      </div>

      <button onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'none', border: 'none', color: '#7a7058', fontFamily: "'Special Elite', monospace", fontSize: 11, cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        <ArrowLeft size={14} /> RETURN
      </button>

      <div style={{ margin: '0 12px 24px', maxWidth: 920, marginInline: 'auto', padding: 14, ...D.paper }}>
        <PlaySheet
          event={event}
          bounties={bounties}
          factions={factions}
          mcsNodes={mcsNodes}
          isGm={role.isFullGM}
          isCoGm={role.isCoGm}
          coGmFactionId={role.coGmFactionId}
          playSheetMode={orgFeatures.play_sheet_mode || 'all'}
          onRefresh={fetchAll}
        />
      </div>
    </div>
  )
}
